import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

interface DeepLinkHandler {
  onInvite?: (token: string) => void;
}

/**
 * Hook to handle deep linking for team invitations
 * Supports both mobile app deep links (noor://) and web URLs (?invite=token)
 */
export const useDeepLinking = ({ onInvite }: DeepLinkHandler) => {
  useEffect(() => {
    // Only set up mobile deep linking on native platforms
    if (Capacitor.isNativePlatform()) {
      console.log('[DeepLink] Setting up native deep link listener');
      
      let listenerHandle: any = null;
      
      // Set up listener asynchronously
      const setupListener = async () => {
        listenerHandle = await CapApp.addListener('appUrlOpen', (data) => {
          const url = data.url;
          console.log('[DeepLink] App opened with URL:', url);
          
          try {
            // Parse deep link URL: noor://invite?token=abc123
            const urlObj = new URL(url);
            
            // Check if it's an invitation deep link
            if (urlObj.protocol === 'noor:' && urlObj.hostname === 'invite') {
              const token = urlObj.searchParams.get('token');
              
              if (token && onInvite) {
                console.log('[DeepLink] Invitation token found:', token);
                onInvite(token);
              } else {
                console.warn('[DeepLink] No token found in invitation URL');
              }
            }
          } catch (error) {
            console.error('[DeepLink] Error parsing deep link URL:', error);
          }
        });
      };
      
      setupListener();

      // Cleanup listener on unmount
      return () => {
        if (listenerHandle) {
          listenerHandle.remove();
        }
      };
    }
  }, [onInvite]);

  // Handle web URL parameters on initial load
  useEffect(() => {
    // Check for ?invite=TOKEN in URL on web or initial app load
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');
    
    if (inviteToken && onInvite) {
      console.log('[DeepLink] Web invitation token found:', inviteToken);
      onInvite(inviteToken);
      
      // Clean up URL parameter after processing (optional)
      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [onInvite]);
};
